/**
 * Authentication Helper Utilities
 * Centralized authentication helpers to ensure consistent auth across all components
 * 
 * SECURITY: All API calls MUST use these helpers to include proper authentication
 */

/**
 * Get authentication headers for API requests
 * Includes both Authorization token and X-Tenant-ID for tenant isolation
 * 
 * @returns Headers object with Authorization and X-Tenant-ID
 */
export const getAuthHeadersSync = (): Record<string, string> => {
  const token = localStorage.getItem('astrapeak_auth_token');
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    // Support both snake_case (backend standard) and camelCase (legacy) for compatibility
    'X-Tenant-ID': user?.tenant_id || user?.tenantId || '',
    'Content-Type': 'application/json'
  };
};

/**
 * Async version for compatibility with existing async code
 */
export const getAuthHeadersAsync = async (): Promise<Record<string, string>> => {
  return getAuthHeadersSync();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('astrapeak_auth_token');
  return !!token;
};

/**
 * Get current tenant ID
 */
export const getCurrentTenantId = (): string | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    const user = JSON.parse(userStr);
    // Support both snake_case (backend standard) and camelCase (legacy) for compatibility
    return user?.tenant_id || user?.tenantId || null;
  } catch {
    return null;
  }
};

/**
 * Create fetch options with authentication
 * 
 * @param options - Additional fetch options
 * @returns Fetch options with auth headers included
 */
export const createAuthFetchOptions = (options: RequestInit = {}): RequestInit => {
  const authHeaders = getAuthHeadersSync();
  
  return {
    ...options,
    headers: {
      ...authHeaders,
      ...options.headers
    }
  };
};
